import React, { useCallback, useState } from 'react';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { useObservableState } from 'observable-hooks';
import { map } from 'rxjs/operators';
import { useTranslation } from 'react-i18next';
import { DASHBOARD } from '../../../router/routes';
import { RootState } from '../../../store/store';
import Event from '../../../interfaces/event';
import { useService } from '../../../services/services';
import { downloadFile, route } from '../../../services/utils';
import { LinkComponent } from '../../../components/form/elements/link';
import Spinner from '../../../components/common/spinner';
import '../../../components/form/elements/wysiwyg/wysiwyg.css';
import SANITIZE_OPTIONS from '../../../components/form/elements/wysiwyg/sanitize-options';
import {
  faArrowDown,
  faFileExcel,
  faLongArrowLeft,
} from '@fortawesome/pro-regular-svg-icons';
import { faClock, faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import formCss from '../../../components/form/form.module.css';
import css from '../index.module.css';
import eventCss from './index.module.css';

export default function ShowEvent() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { eventService, fileService } = useService();
  const [disabled, setDisabled] = useState(false);
  const { selectedFarm } = useSelector((state: RootState) => ({
    selectedFarm: state.global.farm,
  }));
  const [event] = useObservableState<Event>(
    () =>
      eventService.publicShow(id).pipe(
        map((item: Event) => ({
          ...item,
          created_at: moment(item.created_at).format('DD.MM.YYYY'),
          date: moment(item.date).format('DD.MM.YYYY'),
          time: moment.utc(item.time).format('HH:mm'),
          content: sanitizeHtml(item.content, SANITIZE_OPTIONS),
        }))
      ),
    null
  );

  const download = useCallback(
    (id: string, name = 'file.pdf') => {
      setDisabled(true);
      fileService.download(id).subscribe((file: Blob) => {
        downloadFile(file, name);
        setDisabled(false);
      });
    },
    [setDisabled, fileService]
  );

  return (
    <div className={formCss['form']}>
      <div className={formCss['header']}>
        <LinkComponent
          label="back"
          icon={faLongArrowLeft}
          linkTo={route(DASHBOARD, { farmId: selectedFarm.id })}
        />
        <h2>{event?.created_at}</h2>
      </div>

      <div className={`${formCss['body']} ${css['opened-article']}`}>
        {!event?.content ? (
          <div className={formCss['loading']}>
            <div>{t('Loading')}</div>
            <Spinner visible={true} size="lg" />
          </div>
        ) : (
          <>
            <h1 data-name="event-title">{event.title}</h1>
            {event.date && event.time && (
              <div className={eventCss['event-description']}>
                <h3>
                  <FontAwesomeIcon icon={faClock} />
                  {event.date}. {t('pages.dashboard.calendar.hours')}{' '}
                  {typeof event.time === 'string' && event.time}
                </h3>
                <h3>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  {event.place}
                </h3>
              </div>
            )}
            <div
              className="ql-editor user-output"
              dangerouslySetInnerHTML={{ __html: event?.content }}
            />
            {event?.attachment?.length ? (
              <div className={eventCss['event-description']}>
                <h4 className={eventCss['event-description-title']}>
                  {t('pages.dashboard.calendar.attachment')}:
                </h4>
                <ul className={eventCss['event-files']}>
                  {event?.attachment.map((attachment) => (
                    <li
                      key={`file-${attachment?.id}`}
                      onClick={() => download(attachment.id, attachment.name)}
                    >
                      {attachment.name}
                      {disabled ? (
                        <Spinner
                          visible={true}
                          size="sm"
                          className={eventCss['event-spinner']}
                        />
                      ) : (
                        <>
                          <FontAwesomeIcon
                            icon={faArrowDown}
                            className={eventCss['event-small']}
                          />
                          <FontAwesomeIcon
                            icon={faFileExcel}
                            className={eventCss['event-big']}
                          />
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
